/**
 * DetailsPanel — the module/edge inspector, as a standalone component.
 *
 * Deliberately SEPARATE from the board: the board renders the diagram; this
 * inspects whatever is selected. A composition wires them through a shared
 * selection (selectedId / selectedEdgeId + onSelect callbacks) and may place
 * this anywhere — in a right rail, a drawer, a popover — or omit it entirely.
 *
 * It owns its own derivation (indexes + fully-expanded space tree) from the
 * shared compiled `model` + resolved `root`, so it leaks nothing back into the
 * board. The board never needs to know the panel exists, and vice-versa.
 */
import { useMemo, type ReactElement } from "react";
import type { ArchitectureModel, StableId } from "@archlang/scene/arch-model";
import { asSource, type SpaceLike } from "@archlang/scene/board";
import { Sidebar, type SidebarTab } from "../components/Sidebar.js";
import { buildModelIndexes, buildBoardIndexes } from "./view-indexes.js";
import { findBoardEdge } from "./findBoardEdge.js";

export interface DetailsPanelProps {
  /** The shared compiled model (same instance the board renders). */
  readonly model: ArchitectureModel;
  /** The resolved space tree from the shared layout — a `BoardSpace` or a lazy
   *  `SpaceSource`. Used only to index nested entities; positions are ignored. */
  readonly root: SpaceLike;
  /** Currently-selected module id (from the shared selection). */
  readonly selectedId: string | null;
  /** Currently-selected edge id (from the shared selection). */
  readonly selectedEdgeId: string | null;
  /** Select a module — feed this back into the shared selection. */
  readonly onSelectModule: (id: string | null) => void;
  /** Select an edge — feed this back into the shared selection. */
  readonly onSelectEdge: (edgeId: string | null) => void;
}

export function DetailsPanel(props: DetailsPanelProps): ReactElement {
  const { model, root, selectedId, selectedEdgeId } = props;

  // Fully-expand the space tree once so every nested space is indexable (the
  // board itself renders lazily; the inspector needs the whole tree).
  const fullRoot = useMemo(() => asSource(root).at([], Infinity), [root]);
  const indexes = useMemo(() => buildModelIndexes(model), [model]);
  const boardIndexes = useMemo(() => buildBoardIndexes(fullRoot, indexes), [fullRoot, indexes]);

  const selectedModule = selectedId ? indexes?.moduleById.get(selectedId) ?? null : null;
  const selectedEdge = selectedEdgeId ? findBoardEdge(fullRoot, selectedEdgeId) : null;

  return (
    <Sidebar
      selectedModule={selectedModule}
      selectedEdge={selectedEdge}
      indexes={indexes}
      boardIndexes={boardIndexes}
      root={fullRoot}
      focalPath={[]}
      processes={model.processes}
      diagnostics={[]}
      activeTab={"details" as SidebarTab}
      onSelectModule={(id: StableId) => props.onSelectModule(id)}
      onSelectEdge={props.onSelectEdge}
    />
  );
}
